// src/axiosConfig.js
import axios from 'axios';
import i18n from './i18n';

// -------------------- API base --------------------
// Support the env var names you mentioned:
// - REACT_APP_API_BASE_URL  (recommended: should include /api or site root)
// - REACT_APP_API_BASE      (legacy)
// Fallback to same-origin nginx proxy: /api/
const RAW_API_BASE =
  (typeof process !== 'undefined' &&
    process.env &&
    (process.env.REACT_APP_API_BASE_URL ||
      process.env.REACT_APP_API_BASE)) ||
  '/api/';

// Normalize so we ALWAYS end up with an API root that ends with "/api/"
// Examples:
//   "/api"                         -> "/api/"
//   "/api/"                        -> "/api/"
//   "https://x.y"                  -> "https://x.y/api/"
//   "https://x.y/"                 -> "https://x.y/api/"
//   "https://x.y/api"              -> "https://x.y/api/"
//   "https://x.y/api/"             -> "https://x.y/api/"
//   "precision.cranial.../api"     -> "https://precision.../api/"
function normalizeApiBase(base) {
  if (!base) return '/api/';

  let s = String(base).trim();
  if (!s) return '/api/';

  // If they put hostname without scheme
  if (s.includes('.') && !/^https?:\/\//i.test(s) && !s.startsWith('/')) {
    s = `https://${s}`;
  }

  // Relative path case
  if (s.startsWith('/')) {
    // ensure "/api/" exactly
    if (!s.endsWith('/')) s += '/';
    // if someone sets "/" accidentally, force "/api/"
    if (s === '//') return '/api/';
    if (s === '/') return '/api/';
    // if they set "/api/" already, keep it
    return s;
  }

  // Absolute URL case
  try {
    const u = new URL(s);
    let p = u.pathname || '/';

    // normalize trailing slash
    if (!p.endsWith('/')) p += '/';

    // If path is root, append api
    if (p === '/') {
      u.pathname = '/api/';
      return u.toString();
    }

    // If path already contains /api/, keep it (ensure trailing slash)
    if (p === '/api/' || p.endsWith('/api/')) {
      u.pathname = p;
      return u.toString();
    }

    // If path is exactly "/api", make it "/api/"
    if (p === '/api/') {
      u.pathname = '/api/';
      return u.toString();
    }

    // If they gave site root-ish (like "/something"), assume they meant API root is that path,
    // but if it doesn't end with "/api/", append it.
    // Example: https://host -> /api/
    // Example: https://host/svc -> /svc/api/
    if (!p.endsWith('/api/')) {
      u.pathname = p.replace(/\/+$/, '') + '/api/';
    }

    return u.toString();
  } catch {
    // Final fallback
    return '/api/';
  }
}

const API_BASE = normalizeApiBase(RAW_API_BASE);

// -------------------- axios instance --------------------
const instance = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  withCredentials: true,
  timeout: 45000,
});

// Django CSRF cookie/header mapping
instance.defaults.xsrfCookieName = 'csrftoken';
instance.defaults.xsrfHeaderName = 'X-CSRFToken';

// ---------- Utilities ----------
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

// ---------- CJK helpers ----------
function isCJKCodePoint(cp) {
  if (
    (cp >= 0x4e00 && cp <= 0x9fff) ||
    (cp >= 0x3400 && cp <= 0x4dbf) ||
    (cp >= 0xf900 && cp <= 0xfaff) ||
    (cp >= 0x3040 && cp <= 0x309f) ||
    (cp >= 0x30a0 && cp <= 0x30ff) ||
    (cp >= 0x3100 && cp <= 0x312f) ||
    (cp >= 0x31a0 && cp <= 0x31bf) ||
    (cp >= 0x3000 && cp <= 0x303f) ||
    (cp >= 0xff00 && cp <= 0xffef)
  ) return true;
  if (cp >= 0x20000 && cp <= 0x2ebef) return true;
  return false;
}

function stringHasCJK(s) {
  if (typeof s !== 'string' || s.length === 0) return false;
  for (let i = 0; i < s.length; ) {
    const cp = s.codePointAt(i);
    if (cp !== undefined && isCJKCodePoint(cp)) return true;
    i += cp && cp > 0xffff ? 2 : 1;
  }
  return false;
}

function valueHasCJK(v) {
  if (v == null) return false;
  if (typeof v === 'string') return stringHasCJK(v);
  if (Array.isArray(v)) return v.some(valueHasCJK);
  if (typeof v === 'object') {
    if (typeof File !== 'undefined' && v instanceof File) return false;
    if (typeof Blob !== 'undefined' && v instanceof Blob) return false;
    return Object.values(v).some(valueHasCJK);
  }
  return false;
}

const FIELD_ALLOWLIST = null;

function payloadHasCJK(data) {
  if (!data) return false;

  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    for (const [k, v] of data.entries()) {
      if (FIELD_ALLOWLIST && !FIELD_ALLOWLIST.includes(k)) continue;
      if (typeof v === 'string' && stringHasCJK(v)) return true;
    }
    return false;
  }

  if (typeof data === 'string') return stringHasCJK(data);

  if (FIELD_ALLOWLIST && typeof data === 'object') {
    return FIELD_ALLOWLIST.some((k) => valueHasCJK(data[k]));
  }

  return valueHasCJK(data);
}

const WRITE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

const CJK_ALLOWED_PREFIXES = [
  'translate/',
  'patient-messages/',
  'department-messages/',
];

function getNormalizedPath(url) {
  if (!url || typeof url !== 'string') return '';
  let path = url.replace(/^https?:\/\/[^/]+/, '');
  if (path.startsWith('/')) path = path.slice(1);
  if (path.startsWith('api/')) path = path.slice(4);
  return path;
}

// ---------- Interceptors ----------
instance.interceptors.request.use(
  (config) => {
    // Ensure baseURL always set (some callers may override)
    if (!config.baseURL) config.baseURL = API_BASE;

    // Attach CSRF if present
    const token = getCookie('csrftoken');
    if (token) config.headers['X-CSRFToken'] = token;

    const method = (config.method || '').toLowerCase();
    const path = getNormalizedPath(config.url || '');

    const cjkAllowed =
      CJK_ALLOWED_PREFIXES.some((prefix) => path.startsWith(prefix)) ||
      /^patients\/\d+\/recommendations\/?$/.test(path);

    if (WRITE_METHODS.has(method) && !cjkAllowed && payloadHasCJK(config.data)) {
      const err = new Error(i18n.t('warn.noCJK'));
      err.name = 'CJKBlockedError';
      return Promise.reject(err);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

let redirecting = false;

instance.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error?.name === 'CJKBlockedError') {
      alert(i18n.t('warn.noCJK'));
      return Promise.reject(error);
    }

    const status = error?.response?.status;
    const url = error?.config?.url || '';

    const path = typeof url === 'string' ? url.replace(/^https?:\/\/[^/]+/, '') : '';
    const currentPath = typeof window !== 'undefined' ? window.location.pathname || '' : '';

    const onDeptPortal =
      typeof window !== 'undefined' && currentPath.startsWith('/department-messages');

    const isDeptApi =
      path.startsWith('department-messages/') ||
      path.startsWith('/department-messages/');

    if (status === 429) return Promise.reject(error);

    if (status === 401 && !redirecting) {
      if (onDeptPortal || isDeptApi) return Promise.reject(error);

      const onLogin = currentPath.startsWith('/login');
      if (!onLogin) {
        redirecting = true;
        window.location.assign('/login');
      }
    }

    return Promise.reject(error);
  }
);

export default instance;
export { API_BASE };
